/**
 * Deployed-version helpers (power#1224).
 *
 * The running build's identity is `VITE_APP_VERSION`, which the deploy
 * workflows already set to the commit sha. The deployed build's identity comes
 * from `/version.json`, emitted by the `studio1-version-manifest` Vite plugin.
 */

import { logger } from "@/services/logger";

/** Placeholder value in `.env.example` / `.env.local` — never a real build. */
const LOCAL_VERSION_SENTINEL = "0.1.0";

export const RUNNING_VERSION: string | undefined = import.meta.env
  .VITE_APP_VERSION;

/**
 * Only watch for new builds where the question is meaningful: a real deployed
 * bundle. Locally, Vite's HMR owns reloading and the sentinel version would
 * make every poll look like a new deploy.
 */
export function isVersionWatchEnabled(): boolean {
  return (
    Boolean(RUNNING_VERSION) &&
    RUNNING_VERSION !== LOCAL_VERSION_SENTINEL &&
    import.meta.env.PROD
  );
}

export interface DeployedVersion {
  version: string;
  builtAt: string | null;
}

/**
 * Fetch the deployed build id, or null if it cannot be established.
 *
 * Never throws: a failed poll must be a no-op, not an app-level error.
 *
 * The content-type check is load-bearing, not defensive boilerplate. The
 * CloudFront distributions map BOTH 403 and 404 to `/index.html` with HTTP 200
 * (SPA routing — see docs-alpha `infra/17-web.yaml`), so a missing or
 * mis-synced `version.json` arrives as HTML with `res.ok === true`.
 */
export async function fetchDeployedVersion(): Promise<DeployedVersion | null> {
  try {
    // `cache: no-store` covers the browser. It does NOT cover the edge: the
    // distributions use Managed-CachingOptimized, whose cache key excludes
    // query strings, so the cachebust param below is a browser-level measure
    // only. Correct edge behaviour depends entirely on version.json being
    // synced with `no-cache` headers by the deploy workflows.
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    if (!res.headers.get("content-type")?.includes("application/json")) {
      return null;
    }
    const body: unknown = await res.json();
    if (
      typeof body !== "object" ||
      body === null ||
      typeof (body as DeployedVersion).version !== "string"
    ) {
      return null;
    }
    const parsed = body as DeployedVersion;
    return {
      version: parsed.version,
      builtAt: typeof parsed.builtAt === "string" ? parsed.builtAt : null,
    };
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------------- *
 * Reload bookkeeping
 *
 * Guards against reload loops. A deploy is not atomic across CloudFront's
 * async invalidation, so a tab can briefly see an index.html and a version.json
 * that disagree. Without these guards that skew reloads the tab repeatedly.
 * ------------------------------------------------------------------------- */

const RELOADED_FOR_KEY = "s1:reloadedForVersion";
const RELOADED_AT_KEY = "s1:reloadedForVersionAt";
/** Floor between reloads, regardless of what the poll reports. */
const RELOAD_COOLDOWN_MS = 60_000;

function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null; // private mode / storage disabled
  }
}

/** The build we already reloaded for in this tab, if any. */
export function reloadedForVersion(): string | null {
  return readSession(RELOADED_FOR_KEY);
}

/**
 * True when we already reloaded this tab for `version`.
 *
 * Once is enough, regardless of elapsed time: we booted after that reload, so
 * either we are now running it, or the served bundle and version.json disagree
 * — and in the second case reloading again would loop (see
 * `checkPostReloadSanity`).
 */
export function hasAlreadyReloadedFor(version: string): boolean {
  return reloadedForVersion() === version;
}

export function isWithinReloadCooldown(): boolean {
  const at = Number(readSession(RELOADED_AT_KEY) ?? 0);
  if (!Number.isFinite(at) || at <= 0) return false;
  return Date.now() - at < RELOAD_COOLDOWN_MS;
}

export function markReloadedFor(version: string): void {
  try {
    sessionStorage.setItem(RELOADED_FOR_KEY, version);
    sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
  } catch {
    /* storage disabled — the cooldown degrades, the app still works */
  }
}

/**
 * Called once on boot. If we reloaded for a build and came back running
 * something else, the served bundle and version.json disagree; reloading again
 * would loop forever. Log it and stop treating that build as pending.
 *
 * @returns the version to ignore for the rest of this tab's life, if any.
 */
export function checkPostReloadSanity(): string | null {
  const target = reloadedForVersion();
  if (!target) return null;
  if (target === RUNNING_VERSION) return null;
  logger.warn(
    "Reloaded for a new build but still running a different one — " +
      "served bundle and version.json disagree; stopping version watch for it",
    { reloadedFor: target, running: RUNNING_VERSION },
  );
  return target;
}
